import * as React from 'react';
import ReactPlayer from 'react-player';
import classNames from 'classnames';

import CardVideo from '../CardVideo';

import { Image as ImageType, renditions } from '../../types';
import Image, { MediaQuery } from "../Image/Image";

import s from './MediaCluster.module.scss';

export interface MediaClusterProps {
    largeImage: ImageType;
    largeVideoUrl: string;
    largeVideoFile: any;
    smallBottomImage: ImageType;
    smallBottomVideoUrl: string;
    smallBottomVideoFile: any;
    smallTopImage: ImageType;
    smallTopVideoUrl: string;
    smallTopVideoFile: any;
}

const MediaCluster: React.FC<MediaClusterProps> = ({
    largeImage,
    largeVideoUrl,
    largeVideoFile,
    smallBottomImage,
    smallBottomVideoUrl,
    smallBottomVideoFile,
    smallTopImage,
    smallTopVideoUrl,
    smallTopVideoFile,
}) => {

    type GetMediaQueriesTypes = {
        renditions: renditions;
        modifier: string;
    };

    const getMediaQueries = ({
        modifier,
        renditions,
    }: GetMediaQueriesTypes): MediaQuery[] => {
        let mediaQueries = [];
        if (modifier === "large") {
            mediaQueries = [
                { minWidth: 1680, src: renditions?.large.src },
                { minWidth: 960, src: renditions?.medium.src },
                { minWidth: 768, src: renditions?.large.src },
                { minWidth: 480, src: renditions?.medium.src },
                { minWidth: 360, src: renditions?.small.src },
            ];
        }

        if (modifier === "smallTop") {
            mediaQueries = [
                { minWidth: 1280, src: renditions?.medium.src },
                { minWidth: 1100, src: renditions?.medium.src },
                { minWidth: 960, src: renditions?.small.src },
                { minWidth: 768, src: renditions?.large.src },
                { minWidth: 480, src: renditions?.medium.src },
                { minWidth: 360, src: renditions?.small.src },
            ];
        }

        if (modifier === "smallBottom") {
            mediaQueries = [
                { minWidth: 1680, src: renditions?.medium.src },
                { minWidth: 960, src: renditions?.small.src },
                { minWidth: 768, src: renditions?.large.src },
                { minWidth: 480, src: renditions?.medium.src },
                { minWidth: 360, src: renditions?.small.src },
            ];
        }

        return mediaQueries.filter((x) => x.src);
    };

    return (
        <div className={s.Root}>
            <div className={s.Container}>
                {(largeImage ||
                    largeVideoUrl ||
                    largeVideoFile) && (
                        <MediaClusterItem
                            videoUrl={largeVideoUrl}
                            videoFile={largeVideoFile}
                            image={largeImage.renditions?.thumb || largeImage}
                            modifier={"Large"}
                            mediaQueries={getMediaQueries({
                                modifier: "large",
                                renditions: largeImage.renditions,
                            })}
                        />
                    )}
                <div className={s.SmallWrapper}>
                    {(smallTopImage ||
                        smallTopVideoUrl ||
                        smallTopVideoFile) && (
                            <MediaClusterItem
                                videoUrl={smallTopVideoUrl}
                                videoFile={smallTopVideoFile}
                                image={
                                    smallTopImage?.renditions?.thumb ||
                                    smallTopImage
                                }
                                modifier={"Small-Top"}
                                mediaQueries={getMediaQueries({
                                    modifier: "smallTop",
                                    renditions: smallTopImage.renditions,
                                })}
                            />
                        )}
                    {(smallBottomImage ||
                        smallBottomVideoUrl ||
                        smallBottomVideoFile) && (
                            <MediaClusterItem
                                videoUrl={smallBottomVideoUrl}
                                videoFile={smallBottomVideoFile}
                                image={
                                    smallBottomImage?.renditions?.thumb ||
                                    smallBottomImage
                                }
                                modifier={"Small-Bottom"}
                                mediaQueries={getMediaQueries({
                                    modifier: "smallBottom",
                                    renditions: smallBottomImage.renditions,
                                })}
                            />
                        )}
                </div>
            </div>
        </div>
    );
};

const MediaClusterItem = ({
    videoUrl,
    videoFile,
    image,
    modifier,
    mediaQueries,
}) => {
    const [isReady, setReady] = React.useState(false);

    const handleReady = () => {
        if(typeof document !== 'undefined') {
            document.querySelectorAll('iframe').forEach(i => i.tabIndex = -1)
            document.querySelectorAll('video').forEach(i => i.tabIndex = -1);
        }
        setReady(true);
    };

    const classes = classNames(s.Media, {
        [s.MediaScaled]: videoUrl && videoUrl.indexOf('youtube') > -1,
        [s.MediaVideo]: !!videoUrl,
        [s.MediaReady]: isReady,
        [s.MediaLarge]: modifier === 'Large',
        [s.MediaSmallTop]: modifier === 'Small-Top',
        [s.MediaSmallBottom]: modifier === 'Small-Bottom',
    });
    return (
        <div className={classes}>
            <div className={s.MediaContainer}>
                {image && (
                    <Image
                        image={image}
                        className={s.Image}
                        mediaQueries={mediaQueries}
                    />
                )}

                {videoUrl && (
                    <ReactPlayer
                        className={s.Player}
                        url={videoUrl}
                        width="100%"
                        height="100%"
                        loop={true}
                        muted={true}
                        playsinline={true}
                        playing
                        config={{
                            youtube: {
                                playerVars: {
                                    showinfo: 0,
                                    autoplay: 1,
                                    loop: 1,
                                    controls: 0,
                                    modestbranding: 1,
                                    mute: 1,
                                },
                            },
                            vimeo: {
                                playerOptions: {
                                    autoplay: 1,
                                    muted: 1,
                                    loop: 1,
                                    dnt: 1,
                                },
                            },
                        }}
                        onReady={handleReady}
                    />
                )}

                {videoFile && 'url' in videoFile && (
                    <ReactPlayer
                        className={s.Player}
                        url={videoFile.url}
                        playing={true}
                        muted
                        config={{
                            file: {
                                attributes: {
                                    playsInline: 1,
                                    autoPlay: 1,
                                    muted: 1,
                                    loop: 1,
                                },
                            },
                        }}
                        width="100%"
                        height="100%"
                        onReady={handleReady}
                    />
                )}
            </div>
        </div>
    );
};

export default MediaCluster;
